// Firestore access for the GSolar PPE Details tab. All maths lives in
// ppeCalculator.js; this file only reads/writes and enforces input rules.
import {
  addDoc, collection, doc, getDoc, getDocs, query, serverTimestamp, setDoc, updateDoc, where, writeBatch
} from "firebase/firestore";
import { auth, db } from "../firebase";
import {
  ENTRY_KINDS, PPE_COMPANIES, PPE_INVOICE_CLIENT_CODES, PPE_RATE_PER_EMPLOYEE, PURCHASE_STATUSES, fundFromInvoiceLines, monthsBetween, round,
  summarizePeriod, validateSettlement
} from "./ppeCalculator";

const ENTRIES = "gsolar_ppe_entries";
const SETTLEMENTS = "gsolar_ppe_settlements";
const LOCKS = "gsolar_ppe_locks";
const SNAPSHOTS = "approved_invoice_snapshots";

export const PPE_EDITOR_EMAILS = ["admin@almhr.com", "laeeq@almhr.com", "nazim@almhr.com"];
export const PPE_VIEWER_EMAILS = [...PPE_EDITOR_EMAILS, "krashid@gsolar.pk"];
export const PPE_REOPEN_EMAILS = ["admin@almhr.com"];

const currentEmail = () => auth.currentUser?.email?.toLowerCase() || "";
export const canViewPpe = () => PPE_VIEWER_EMAILS.includes(currentEmail());
export const canEditPpe = () => PPE_EDITOR_EMAILS.includes(currentEmail());
export const canReopenPpe = () => PPE_REOPEN_EMAILS.includes(currentEmail());

const actor = () => ({ uid: auth.currentUser?.uid || null, email: auth.currentUser?.email || null });
const text = (value) => String(value ?? "").trim();

function assertCompany(company) {
  if (!PPE_COMPANIES.some((item) => item.code === company)) throw new Error("Select GSolar Power or VS Alpha.");
}

function assertPeriod(period) {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(text(period))) throw new Error("Enter the month as YYYY-MM.");
}

export async function listEntries(company) {
  assertCompany(company);
  const result = await getDocs(query(collection(db, ENTRIES), where("company", "==", company)));
  return result.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function listSettlements(company) {
  assertCompany(company);
  const result = await getDocs(query(collection(db, SETTLEMENTS), where("company", "==", company)));
  return result.docs.map((item) => ({ id: item.id, ...item.data() }));
}

function buildEntryFields(input) {
  const kind = input.kind;
  if (!Object.values(ENTRY_KINDS).includes(kind)) throw new Error("Choose Fund received or Material purchase.");
  assertCompany(input.company);
  const entryDate = text(input.entryDate);
  const period = text(input.period) || entryDate.slice(0, 7);
  assertPeriod(period);
  if (entryDate && entryDate.slice(0, 7) !== period) throw new Error("The date must fall inside the selected month.");
  const amount = round(input.amount);
  if (!(amount > 0)) throw new Error("Enter an amount greater than zero.");
  const description = text(input.description);
  if (kind === ENTRY_KINDS.PURCHASE && !description) throw new Error("Describe what was purchased.");

  const fields = {
    company: input.company,
    kind,
    period,
    entryDate,
    dateEstimated: Boolean(input.dateEstimated) && !entryDate,
    amount,
    description: description || (kind === ENTRY_KINDS.FUND ? "PPE funds received from client" : ""),
    sentTo: text(input.sentTo),
    employeeName: text(input.employeeName),
    supplier: text(input.supplier),
    billNumber: text(input.billNumber),
    requestRef: text(input.requestRef),
    note: text(input.note)
  };
  if (kind === ENTRY_KINDS.PURCHASE) {
    const status = text(input.purchaseStatus) || "PURCHASED";
    if (!PURCHASE_STATUSES.includes(status)) throw new Error("Choose a valid purchase status.");
    fields.purchaseStatus = status;
  } else {
    fields.headCount = input.headCount === "" || input.headCount == null ? null : round(input.headCount);
  }
  return fields;
}

export async function createEntry(input) {
  const fields = buildEntryFields(input);
  const who = actor();
  const saved = {
    ...fields,
    entryStatus: "ACTIVE",
    source: input.source || "MANUAL",
    seq: Date.now(),
    createdBy: who.uid, createdByEmail: who.email, createdAt: serverTimestamp()
  };
  const ref = await addDoc(collection(db, ENTRIES), saved);
  return { id: ref.id, ...saved };
}

export async function updateEntry(entryId, input) {
  const fields = buildEntryFields(input);
  const who = actor();
  await updateDoc(doc(db, ENTRIES, entryId), {
    ...fields,
    updatedBy: who.uid, updatedByEmail: who.email, updatedAt: serverTimestamp()
  });
}

export async function rejectEntry(entryId, reason) {
  if (!text(reason)) throw new Error("A reason is required to reject an entry.");
  const who = actor();
  await updateDoc(doc(db, ENTRIES, entryId), {
    entryStatus: "REJECTED",
    rejectedReason: text(reason),
    rejectedBy: who.uid, rejectedByEmail: who.email, rejectedAt: serverTimestamp()
  });
}

export async function restoreEntry(entryId) {
  const who = actor();
  await updateDoc(doc(db, ENTRIES, entryId), {
    entryStatus: "ACTIVE",
    rejectedReason: null,
    restoredBy: who.uid, restoredByEmail: who.email, restoredAt: serverTimestamp()
  });
}

// Creates one FUND entry per month from the PPE line of each APPROVED invoice,
// so "funds received" always matches what was actually billed. Months that
// already have a fund entry (imported or manual) are left alone.
export async function pullFundsFromInvoices(company) {
  assertCompany(company);
  const [snapshots, existing] = await Promise.all([
    getDocs(query(collection(db, SNAPSHOTS), where("clientCode", "in", PPE_INVOICE_CLIENT_CODES))),
    listEntries(company)
  ]);
  const fundedMonths = new Set(existing.filter((entry) => entry.kind === ENTRY_KINDS.FUND && entry.entryStatus !== "REJECTED").map((entry) => entry.period));
  const byMonth = new Map();
  snapshots.docs.forEach((item) => {
    const data = item.data();
    const ppe = fundFromInvoiceLines(data.lines);
    if (!(ppe > 0) || !data.accountingPeriod) return;
    const current = byMonth.get(data.accountingPeriod) || { amount: 0, invoices: [] };
    current.amount += ppe;
    current.invoices.push(`${data.invoiceNumber} (${round(ppe / PPE_RATE_PER_EMPLOYEE)} staff)`);
    byMonth.set(data.accountingPeriod, current);
  });

  const added = [];
  const who = actor();
  for (const [period, info] of [...byMonth.entries()].sort()) {
    if (fundedMonths.has(period)) continue;
    const id = `INV_${company}_${period}`;
    if ((await getDoc(doc(db, ENTRIES, id))).exists()) continue;
    await setDoc(doc(db, ENTRIES, id), {
      company, kind: ENTRY_KINDS.FUND, period, entryDate: "", dateEstimated: false,
      amount: round(info.amount),
      headCount: round(info.amount / PPE_RATE_PER_EMPLOYEE),
      description: `PPE charged in invoices ${info.invoices.join(" + ")}`,
      sentTo: "", employeeName: "", supplier: "", billNumber: "", requestRef: "", note: "",
      entryStatus: "ACTIVE", source: "INVOICE", seq: Date.now(),
      createdBy: who.uid, createdByEmail: who.email, createdAt: serverTimestamp()
    });
    added.push({ period, amount: round(info.amount) });
  }
  return added;
}

export async function settlePeriod({ company, start, end, settledAmount, settledOn, reference, note }) {
  assertCompany(company);
  assertPeriod(start); assertPeriod(end);
  if (!settledOn) throw new Error("Enter the settlement date.");
  const [entries, settlements] = await Promise.all([listEntries(company), listSettlements(company)]);
  if (settlements.some((item) => item.periodStart <= end && item.periodEnd >= start)) {
    throw new Error("This period overlaps one that is already settled.");
  }
  const summary = summarizePeriod({ entries, settlements, start, end });
  const amount = round(settledAmount);
  const problem = validateSettlement({ closing: summary.closing, settledAmount: amount });
  if (problem) throw new Error(problem);

  const who = actor();
  const settlementId = `${company}_${start}_${end}`;
  const batch = writeBatch(db);
  batch.set(doc(db, SETTLEMENTS, settlementId), {
    company, periodStart: start, periodEnd: end,
    openingBalance: summary.opening, funds: summary.funds, purchases: summary.purchases,
    closingBalance: summary.closing, settledAmount: amount, carriedForward: round(summary.closing - amount),
    settledOn, reference: text(reference), note: text(note),
    settledBy: who.uid, settledByEmail: who.email, settledAt: serverTimestamp()
  });
  monthsBetween(start, end).forEach((month) => {
    batch.set(doc(db, LOCKS, `${company}_${month}`), { company, period: month, settlementId, lockedAt: serverTimestamp() });
  });
  await batch.commit();
  return { id: settlementId, ...summary, settledAmount: amount };
}

export async function reopenPeriod(settlement) {
  const batch = writeBatch(db);
  batch.delete(doc(db, SETTLEMENTS, settlement.id));
  monthsBetween(settlement.periodStart, settlement.periodEnd).forEach((month) => {
    batch.delete(doc(db, LOCKS, `${settlement.company}_${month}`));
  });
  await batch.commit();
}

export async function listLockedMonths(company) {
  assertCompany(company);
  const result = await getDocs(query(collection(db, LOCKS), where("company", "==", company)));
  return new Set(result.docs.map((item) => item.data().period));
}
